import { useState } from "react";

import axiosInstance from "../axiosInstance";
import { getImageUrl } from "../imageUrl";

import {
  MdClose,
  MdEmail,
  MdPhone,
  MdDescription,
  MdCheckCircleOutline,
  MdOutlinePendingActions,
  MdImage,
  MdPhotoCamera,
  MdUploadFile,
  MdDeleteOutline,
  MdLockOutline,
} from "react-icons/md";

export default function ViewQueryModel({
  query,
  close,
  refresh,
}) {
  const API = process.env.REACT_APP_API_URL;

  const [updating, setUpdating] = useState(false);

  const [closedRemarks, setClosedRemarks] = useState(
    query?.closed_remarks || ""
  );

  const [inProgressRemarks, setInProgressRemarks] = useState(
    query?.in_progress_remarks || ""
  );

  const [holdRemarks, setHoldRemarks] = useState(
    query?.hold_remarks || ""
  );

  const [localStatus, setLocalStatus] = useState(
    query?.status || "Pending"
  );

  const [statusSaved, setStatusSaved] = useState(
    ["completed", "closed"].includes(
      String(query?.status || "").toLowerCase()
    )
  );



  // =========================================================
  // UPDATE STATUS
  // =========================================================

  const [closedBy, setClosedBy] = useState(
    query?.closed_by || ""
  );

  const [completionImage, setCompletionImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  const getStatusRemarks = () => {
    const normalizedStatus = String(localStatus).toLowerCase();

    if (normalizedStatus === "in progress") return inProgressRemarks;
    if (normalizedStatus === "hold") return holdRemarks;
    if (["completed", "closed"].includes(normalizedStatus)) {
      return closedRemarks;
    }

    return "";
  };

  const setStatusRemarks = (value) => {
    const normalizedStatus = String(localStatus).toLowerCase();

    if (normalizedStatus === "in progress") setInProgressRemarks(value);
    if (normalizedStatus === "hold") setHoldRemarks(value);
    if (["completed", "closed"].includes(normalizedStatus)) {
      setClosedRemarks(value);
    }
  };

  const updateStatus = async () => {
    const requiresRemarks = [
      "In Progress",
      "Hold",
      "Completed"
    ].includes(localStatus);

    const statusRemarks = getStatusRemarks();

    if (requiresRemarks && !statusRemarks.trim()) {
      alert(`Please enter ${localStatus.toLowerCase()} remarks.`);
      return;
    }

    try {
      setUpdating(true);

      const payload = new FormData();
      payload.append("status", localStatus);
      payload.append("status_remarks", statusRemarks.trim());

      if (completionImage && ["completed", "closed"].includes(
        localStatus.toLowerCase()
      )) {
        payload.append("completed_image", completionImage);
      }

      const response = await axiosInstance.post(
        `${API}/api/query/status/${query.id}/`,
        payload
      );

      console.log("Status updated:", response.data);

      setClosedBy(response.data?.closed_by || "-");
      setStatusSaved(
        ["completed", "closed"].includes(
          String(response.data?.status || localStatus).toLowerCase()
        )
      );

      await refresh();

    } catch (error) {
      console.error("Status update error:", error);

      alert(
        error.response?.data?.error ||
        "Unable to update query status. Please try again."
      );
    } finally {
      setUpdating(false);
    }
  };

  // =========================================================
  // STATUS
  // =========================================================

  const status = String(
    localStatus || "Pending"
  );

  const isCompleted =
    status.toLowerCase() === "completed" ||
    status.toLowerCase() === "closed";

  const needsStatusRemarks = [
    "in progress",
    "hold",
    "completed",
    "closed"
  ].includes(status.toLowerCase());

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-5"
      onClick={close}
    >

      {/* =====================================================
          MODAL
      ===================================================== */}

      <div
        className="relative w-full max-w-2xl max-h-[92vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) =>
          e.stopPropagation()
        }
      >

        {/* ===================================================
            HEADER
        =================================================== */}

        <div
          className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-slate-200 bg-white flex-shrink-0"
        >

          <div className="flex items-center gap-3">

            <div
              className="w-10 h-10 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center flex-shrink-0"
            >
              <MdDescription size={22} />
            </div>

            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-800">
                Query Details
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Query ID #{query?.id}
              </p>

            </div>

          </div>

          {/* CLOSE */}

          <button
            type="button"
            onClick={close}
            className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition"
            aria-label="Close"
          >
            <MdClose size={22} />
          </button>

        </div>

        {/* ===================================================
            CONTENT
        =================================================== */}

        <div className="overflow-y-auto">

          <div className="p-5 sm:p-6 space-y-5">

            {/* =================================================
                QUERY TITLE + STATUS
            ================================================= */}

            <div
              className="rounded-xl border border-slate-200 bg-slate-50 p-4"
            >

              <div
                className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3"
              >

                <div className="min-w-0">

                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">
                    Query
                  </p>

                  <h3 className="text-lg font-bold text-slate-800 break-words">
                    {query?.query_heading ||
                      "No heading"}
                  </h3>

                </div>

                {/* STATUS */}

                {isCompleted ? (

                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold whitespace-nowrap"
                  >
                    <MdCheckCircleOutline
                      size={16}
                    />

                    Completed
                  </span>

                ) : (

                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold whitespace-nowrap"
                  >
                    <MdOutlinePendingActions
                      size={16}
                    />

                    {status}
                  </span>

                )}

              </div>

            </div>

            {/* =================================================
                USER INFORMATION
            ================================================= */}

            <div>

              <div className="flex items-center gap-2 mb-3">

                <div
                  className="w-7 h-7 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center"
                >
                  <MdEmail size={16} />
                </div>

                <h3 className="text-sm font-semibold text-slate-700">
                  Contact Information
                </h3>

              </div>

              <div
                className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                {/* EMAIL */}

                {/* <div
                  className="rounded-lg border border-slate-200 bg-white p-3"
                >

                  <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">
                    Email
                  </p>

                  <div className="flex items-center gap-2 mt-1.5">

                    <MdEmail
                      size={17}
                      className="text-slate-400 flex-shrink-0"
                    />

                    <p className="text-sm text-slate-700 break-all">
                      {query?.email || "-"}
                    </p>

                  </div>

                </div> */}

                {/* MOBILE */}

                {/* <div
                  className="rounded-lg border border-slate-200 bg-white p-3"
                >

                  <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">
                    Mobile
                  </p>

                  <div className="flex items-center gap-2 mt-1.5">

                    <MdPhone
                      size={17}
                      className="text-slate-400 flex-shrink-0"
                    />

                    <p className="text-sm text-slate-700">
                      {query?.mobile || "-"}
                    </p>

                  </div>

                </div> */}

              </div>

            </div>

            {/* =================================================
                USER / DEPARTMENT
            ================================================= */}

            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
            >


              <div
                className="rounded-lg border border-slate-200 bg-white p-3"
              >

                <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">
                  Requested Department
                </p>

                <p className="text-sm font-medium text-slate-700 mt-1.5">
                  {query?.requested_department || "-"}
                </p>

              </div>

              {/* USERNAME */}

              <div
                className="rounded-lg border border-slate-200 bg-white p-3"
              >

                <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">
                  Requested Department User
                </p>

                <p className="text-sm font-medium text-slate-700 mt-1.5">
                  {query?.requested_department_user || "-"}
                </p>

              </div>

              {/* DEPARTMENT */}

              {/* <div
                className="rounded-lg border border-slate-200 bg-white p-3"
              >

                <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">
                  Responsible Department
                </p>

                <p className="text-sm font-medium text-slate-700 mt-1.5">
                  {query?.department || "-"}
                </p>

              </div> */}



            </div>

            {/* =================================================
                DESCRIPTION
            ================================================= */}

            <div>

              <div className="flex items-center gap-2 mb-3">

                <div
                  className="w-7 h-7 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center"
                >
                  <MdDescription size={16} />
                </div>

                <h3 className="text-sm font-semibold text-slate-700">
                  Description
                </h3>

              </div>

              <div
                className="rounded-xl border border-slate-200  p-4"
              >

                <p
                  className="text-sm leading-6 text-slate-600 whitespace-pre-wrap break-words"
                >
                  {query?.query_description ||
                    "No description provided."}
                </p>

              </div>

            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* =================================================
              IMAGE / ATTACHMENT
            ================================================= */}

            {query?.image && (

              <div>

                <div className="flex items-center gap-2 mb-3">

                  <div
                    className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"
                  >
                    <MdImage size={17} />
                  </div>

                  <h3 className="text-sm font-semibold text-slate-700">
                    Attachment
                  </h3>

                </div>

                <div
                  className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex justify-center"
                >

                  <button
                    type="button"
                    onClick={() => setImagePreview(query.image)}
                    className="rounded-lg border border-slate-200 bg-white overflow-hidden cursor-zoom-in"
                    aria-label="View query attachment"
                  >
                    <img
                      src={getImageUrl(query.image, API)}
                      alt="Query attachment"
                      className="h-24 w-32 object-contain"
                    />
                  </button>

                </div>

              </div>

            )}

            {query?.completed_image && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <MdImage size={17} />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-700">
                    Completion image
                  </h3>
                </div>

                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setImagePreview(query.completed_image)}
                    className="rounded-lg border border-slate-200 bg-white overflow-hidden cursor-zoom-in"
                    aria-label="View completion image"
                  >
                    <img
                      src={getImageUrl(query.completed_image, API)}
                      alt="Completion attachment"
                      className="h-24 w-32 object-contain"
                    />
                  </button>
                </div>
              </div>
            )}

            </div>

            {/* =================================================
                TIMELINE
            ================================================= */}

            <div
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
            >

              {/* CREATED */}

              <div
                className="rounded-lg border border-slate-200 p-3 bg-white"
              >

                <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">
                  Created Date
                </p>

                <p className="text-sm text-slate-700 mt-1.5">
                  {query?.query_created_time
                    ? new Date(
                      query.query_created_time
                    ).toLocaleString("en-GB")
                    : "-"}
                </p>

              </div>

              {/* CLOSED */}

             {isCompleted &&(

               <div
                className="rounded-lg border border-slate-200 p-3 bg-white"
              >

                <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">
                  Closed Date
                </p>

                <p className="text-sm text-slate-700 mt-1.5">
                  {query?.query_closed_time
                    ? new Date(
                      query.query_closed_time
                    ).toLocaleString("en-GB")
                    : "Not closed"}
                </p>

              </div>
             )}

              {/* ================================================= STATUS REMARKS ================================================= */}

              <div className="space-y-3 sm:col-span-2">

                {inProgressRemarks && status.toLowerCase() !== "in progress" && (
                  <div className="rounded-xl border border-sky-200 bg-sky-50 p-4">
                    <p className="text-[11px] uppercase tracking-wide font-semibold text-sky-600">
                      In Progress Remarks
                    </p>
                    <p className="mt-1.5 text-sm leading-6 text-slate-700 whitespace-pre-wrap break-words">
                      {inProgressRemarks}
                    </p>
                  </div>
                )}

                {holdRemarks && status.toLowerCase() !== "hold" && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <p className="text-[11px] uppercase tracking-wide font-semibold text-amber-600">
                      Hold Remarks
                    </p>
                    <p className="mt-1.5 text-sm leading-6 text-slate-700 whitespace-pre-wrap break-words">
                      {holdRemarks}
                    </p>
                  </div>
                )}

                {needsStatusRemarks && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                        <MdDescription size={16} />
                      </div>
                      <h3 className="text-sm font-semibold text-slate-700">
                        {status} Remarks
                      </h3>
                    </div>

                    <textarea
                      value={getStatusRemarks()}
                      onChange={(e) => setStatusRemarks(e.target.value)}
                      placeholder={`Enter remarks for ${status.toLowerCase()}...`}
                      rows={4}
                      className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700 placeholder:text-slate-400 outline-none resize-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                    />

                    {isCompleted && !statusSaved && (
                      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <MdImage size={17} className="text-indigo-600" />
                          <p className="text-sm font-semibold text-slate-700">
                            Completion image (optional)
                          </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2">
                          <label
                            htmlFor="completion-image-upload"
                            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100"
                          >
                            <MdUploadFile size={18} />
                            Upload image
                          </label>
                          <input
                            id="completion-image-upload"
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => setCompletionImage(e.target.files?.[0] || null)}
                          />

                          <label
                            htmlFor="completion-image-camera"
                            className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-100"
                          >
                            <MdPhotoCamera size={18} />
                            Take photo
                          </label>
                          <input
                            id="completion-image-camera"
                            type="file"
                            accept="image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => setCompletionImage(e.target.files?.[0] || null)}
                          />
                        </div>

                        {completionImage && (
                          <div className="mt-3 flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-2">
                            <img
                              src={URL.createObjectURL(completionImage)}
                              alt="Completion preview"
                              className="h-16 w-16 rounded-md object-cover"
                            />
                            <p className="min-w-0 flex-1 truncate text-xs text-slate-600">
                              {completionImage.name}
                            </p>
                            <button
                              type="button"
                              onClick={() => setCompletionImage(null)}
                              className="rounded-md p-2 text-slate-500 hover:bg-white hover:text-red-600"
                              aria-label="Remove completion image"
                            >
                              <MdDeleteOutline size={19} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    <p className="mt-1 text-xs text-slate-400">
                      This note is saved separately for the selected status.
                    </p>
                  </div>
                )}

                {closedRemarks && status.toLowerCase() !== "completed" && status.toLowerCase() !== "closed" && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <p className="text-[11px] uppercase tracking-wide font-semibold text-emerald-600">
                      Completed Remarks
                    </p>
                    <p className="mt-1.5 text-sm leading-6 text-slate-700 whitespace-pre-wrap break-words">
                      {closedRemarks}
                    </p>
                  </div>
                )}
              </div>


              {/* CLOSED BY */}

              {isCompleted && (

                <div className="rounded-lg border border-slate-200 p-3 bg-white">
                <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">
                  Closed By
                </p>

                <p className="text-sm text-slate-700 mt-1.5 font-semibold">
                  {closedBy || "-"}
                </p>
              </div>
              )

              }

            </div>

          </div>

        </div>

        {imagePreview && (
          <div
            className="fixed inset-0 z-[120] bg-slate-950/80 flex items-center justify-center p-4"
            onClick={() => setImagePreview("")}
          >
            <div
              className="relative max-w-[95vw] max-h-[92vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setImagePreview("")}
                className="absolute -right-3 -top-3 z-10 w-9 h-9 rounded-full bg-white border border-slate-200 shadow-lg flex items-center justify-center text-slate-600 hover:text-red-600"
                aria-label="Close image preview"
              >
                <MdClose size={20} />
              </button>
              <img
                src={getImageUrl(imagePreview, API)}
                alt="Full size attachment"
                className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg bg-white"
              />
            </div>
          </div>
        )}

        {/* ===================================================
            FOOTER
        =================================================== */}

        <div
          className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 px-5 sm:px-6 py-4 border-t border-slate-200 bg-slate-50 flex-shrink-0"
        >

          {/* SECURITY INFO */}

          <div className="flex items-center gap-2">

            <MdLockOutline
              size={16}
              className="text-slate-400"
            />

            <span className="text-xs text-slate-400">
              Query actions are logged
            </span>

          </div>

          {/* BUTTONS */}

          <div
            className="flex flex-col sm:flex-row gap-2"
          >

            {/* CLOSE MODAL */}

            <button
              type="button"
              onClick={close}
              disabled={updating}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-100 disabled:opacity-50 transition"
            >
              <MdClose size={18} />

              Close
            </button>

            {/* MARK AS CLOSED */}

            <div className="flex flex-col sm:flex-row gap-2">

                <select
                  value={localStatus}
                  onChange={(e) => setLocalStatus(e.target.value)}
                  disabled={updating || statusSaved}
                  className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100 disabled:opacity-50"
                  aria-label="Query status"
                >
                  <option value="Pending">Pending</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Hold">Hold</option>
                  <option value="Completed">Completed</option>
                </select>

              <button
                type="button"
                onClick={updateStatus}
                disabled={updating || statusSaved}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-semibold transition"
              >

                {updating ? (

                  <>
                    <span
                      className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"
                    />

                    Updating...
                  </>

                ) : (

                  <>
                    <MdCheckCircleOutline
                      size={18}
                    />

                    Update Status
                  </>

                )}

              </button>

            </div>

          </div>

        </div>

      </div>

    </div>
  );
}